import React, { useState, useRef } from 'react';
import { Wrapper, Menu, Button, MenuItem } from 'react-aria-menubutton';
import CloseIcon from '../svg/close';
import { useTranslate } from '../../contexts/languageContext';
import CampanaIcon from '../svg/Campana';
import CampanaSinLeerIcon from '../svg/CampanaSinLeer';
import {
  useNotifications,
  useDispatchNotifications,
  actionTypes,
  getNotificationText,
} from '../../contexts/notificationsContext';
import styled from 'styled-components/macro';
import Tooltip from '../Tooltip';
import { DialogOverlay, DialogContent } from '@reach/dialog';
import '@reach/dialog/styles.css';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import constantes from '../../resources/constantes.json';
import { getUrl } from '../../utils';
import { Link } from 'react-router-dom';
/**
 * Botón con menu desplegable que informa de las notificaciones recibidas
 *  mientras el usuario utiliza la página. Al cerrar el menú las notificaciones se
 * limpian a la espera de nuevas notificaciones
 */

const sortNotifications = notifications => (a, b) => {
  if (notifications[a].mdhm < notifications[b].mdhm) {
    return 1;
  }
  if (notifications[a].mdhm > notifications[b].mdhm) {
    return -1;
  }
  return 0;
};

export const NotificationButton = props => {
  const isDesktop = useIsDesktop();
  return isDesktop ? (
    <NotificationButtonDesktop {...props} />
  ) : (
    <NotificationButtonMobile {...props} />
  );
};

const NotificationButtonMobile = ({ nomenclator }) => {
  const t = useTranslate();
  const { notifications, unread, read } = useNotifications();
  const dispatch = useDispatchNotifications();
  const [showList, setShowList] = useState(false);
  const toggleMenu = isOpen => () => {
    setShowList(isOpen);
    !isOpen && dispatch({ type: actionTypes.readAll });
  };
  const closeRef = useRef();
  return (
    <div>
      <Tooltip label={t('notificaciones')}>
        <div>
          <StyledCampanaButton onClick={toggleMenu(!showList)}>
            {unread.size > 0 ? (
              <CampanaSinLeerIcon
                number={unread.size > 9 ? '9+' : unread.size}
                aria-hidden="true"
              />
            ) : (
              <CampanaIcon />
            )}
            <div className="visually-hidden">
              {unread.size + ' ' + t('notificacionesSinLeer')}
            </div>
          </StyledCampanaButton>
        </div>
      </Tooltip>
      {showList && (
        <NotificationListMobile
          read={read}
          notifications={notifications}
          unread={unread}
          closeRef={closeRef}
          showList={showList}
          toggleMenu={toggleMenu}
          nomenclator={nomenclator}
          t={t}
        />
      )}
    </div>
  );
};

export const NotificationButtonDesktop = ({ nomenclator }) => {
  const t = useTranslate();
  const { notifications, unread, read } = useNotifications();
  const dispatch = useDispatchNotifications();
  return (
    <div>
      <StyledWrapper
        onSelection={() => {}}
        onMenuToggle={({ isOpen }) => {
          !isOpen && dispatch({ type: actionTypes.readAll });
        }}
      >
          <div>
            <div>
              <StyledButton>
                {unread.size > 0 ? (
                  <CampanaSinLeerIcon
                    number={unread.size > 9 ? '9+' : unread.size}
                    aria-hidden="true"
                  />
                ) : (
                  <CampanaIcon fill="#fff"/>
                )}
                <div className="visually-hidden">
                  {unread.size + ' ' + t('notificacionesSinLeer')}
                </div>
              </StyledButton>
            </div>
          </div>
          <StyledMenu>
            {read.size + unread.size !== 0 ? (
              <Ul>
                {Array.from(unread)
                  .concat(Array.from(read))
                  .sort(sortNotifications(notifications))
                  .map(id => {
                    const { eleccion, mdhm } = notifications[id];
                    const { title, subtitle } = getNotificationText({
                      eleccion,
                      tope: 0,
                      mdhm,
                      t,
                    });
                    return (
                      <Li key={id} isUnread={unread.has(id)}>
                        <SMenuItem value={id}>
                          <SLink
                            to={getUrl({
                              id: 0,
                              eleccion,
                              nomenclator,
                              page: constantes.nombreEleccion[eleccion],
                            })}
                          >
                            <div className="title">{title}</div>
                            <div className="subtitle">{subtitle}</div>
                          </SLink>
                        </SMenuItem>
                      </Li>
                    );
                  })}
              </Ul>
            ) : (
              <div css={"background-color: white; color: #1C283D;"}>{t('noHayNotificaciones')}</div>
            )}
          </StyledMenu>
      </StyledWrapper>
    </div>
  );
};

const NotificationListMobile = ({
  read,
  unread,
  closeRef,
  showList,
  toggleMenu,
  t,
  notifications,
  nomenclator,
}) => (
  <StyledDialogOverlay
    initialFocusRef={closeRef}
    onDismiss={toggleMenu(false)}
    isOpen={showList}
  >
    <StyledDialogContent>
      <CloseRow>
        <Header>
          <span>{t('notificaciones')}</span>
        </Header>
        <CloseButton onClick={toggleMenu(false)} ref={closeRef}>
          <CloseIcon
            width={30}
            height={30}
            fill="#ffffff"
            css={`
              vertical-align: top;
            `}
          />
          <span className="visually-hidden">{t('cerrar')}</span>
        </CloseButton>
      </CloseRow>
      {read.size + unread.size !== 0 ? (
        <ListMobile>
          {Array.from(unread)
            .concat(Array.from(read))
            .sort(sortNotifications(notifications))
            .map(id => {
              const { eleccion, mdhm } = notifications[id];
              const { title, subtitle } = getNotificationText({
                eleccion,
                tope: 0,
                mdhm,
                t,
              });
              return (
                <ListItemMobile isUnread={unread.has(id)}>
                  <SLink
                    to={getUrl({
                      id: 0,
                      eleccion,
                      nomenclator,
                      page: constantes.nombreEleccion[eleccion],
                    })}
                    onClick={toggleMenu(false)}
                  >
                    <div className="title">{title}</div>
                    <div className="subtitle">{subtitle}</div>
                  </SLink>
                </ListItemMobile>
              );
            })}
        </ListMobile>
      ) : (
        <ListItemMobile as="div" >
          {t('noHayNotificaciones')}
        </ListItemMobile>
      )}
    </StyledDialogContent>
  </StyledDialogOverlay>
);

const ListMobile = styled.ul`
  width: 100%;
`;

const ListItemMobile = styled.li`
  padding: 10px;
  background-color: white;
  color: #1C283D;
  margin: 0;
  :nth-child(odd) {
    background-color: white;
  }
  font-size: 1.2em;
  color: #222;
  line-height: 1.35;
  box-sizing: border-box;
  width: 100%;
  > a {
    color: #283f5f;
  }
  &:first-child {
    ${({ isUnread }) => (isUnread ? 'font-weight: bold;' : '')}
  }
  :not(:first-child) {
    pointer-events: none;
  }
`;

const CloseRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  height: 60px;
  @media (max-width: 768px) {
    height: 50px;
  }
`;

const Header = styled.div`
  background: #f6f6f4;
  font-weight: bold;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  font-size: 1.4rem;
  > span {
    padding-left: 10px;
  }
`;
const CloseButton = styled.button`
  background: #1c4183;
  border: none;
  padding: 10px;
  justify-self: flex-end;
  height: 60px;
  @media (max-width: 768px) {
    height: 50px;
  }
`;

const StyledDialogOverlay = styled(DialogOverlay)`
  && {
    background-color: white;
    z-index: 10000;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  && {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    opacity: 1;
    width: 100%;
    display: flex;
    flex-flow: column;
    background-color: white;
    & .focus-visible {
      outline: 2px solid black;
      outline-offset: -2px;
    }
  }
`;

const StyledWrapper = styled(Wrapper)`
  position: relative;
  box-sizing: border-box;
`;

const StyledMenu = styled(Menu)`
  position: absolute;
  z-index: 1;
  right: 0px;
  top: 50px;
  background: white;
  min-width: 350px;
  min-height: 40px;
  max-height: 255px;
  padding: 20px;
  box-shadow: 0 2px 15px 0 rgba(0, 0, 0, 0.2);
  box-sizing: border-box;

  ::before {
    content: '';
    display: inline-block;
    position: absolute;
    right: 13px;
    top: -20px;
    border-width: 10px;
    border-style: solid;
    border-color: transparent transparent white;
  }
`;

const Ul = styled.ul`
  overflow: auto;
  background: white;
  box-sizing: border-box;
  max-height: 195px;
`;
const Li = styled.li`
  &:first-child {
    ${({ isUnread }) => (isUnread ? 'font-weight: bold;' : '')}
  }
  :not(:first-child) {
    pointer-events: none;
  }
`;

const StyledButton = styled(Button)`
  font-weight: bold;
  text-transform: uppercase;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  max-width: 200px;
  margin-left: auto;
  background-color: transparent;
  transition: background-color 0.3s ease 0s;
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const SMenuItem = styled(MenuItem)`
  cursor: pointer;
  font-size: 12px;
  > a {
    color: #283f5f;
  }

  &.focus-visible {
    text-decoration: underline !important;
    outline: none !important;
  }
  :hover {
    a {
      text-decoration: underline;
    }
  }
`;

const SLink = styled(Link)`
  display: block;

  .title {
    font-size: 13px;
    line-height: 1.1;
    @media (max-width: 1024px) {
      font-size: 14px;
      line-height: 2;
    }
  }

  .subtitle {
    font-size: 12px;
    line-height: 1.5;
    color: #222;
    padding-bottom: 10px;
    @media (max-width: 1024px) {
      color: #222;
      line-height: 2;
      padding: 0;
    }
  }
`;

const StyledCampanaButton = styled.button`
  line-height: 0;
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;
